import React, { useState, useRef, useEffect, useContext, useMemo } from 'react';
import { Grid, Paper, Button, Container } from '@material-ui/core';
import Layout from 'components/Layout';
import Title from 'components/Title';
import Wizard from 'components/UI/Wizard';
import i18n from 'i18n-js';
import { Formik, Form } from 'formik';
import { buildUrl } from 'utils';
import { useStyles } from './ReservationRulesetCreate.styled';
import { useParams, useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  PaymentInputs,
  RulesetInputs,
  DetailsInputs,
  ReservationRulesetVariableBufferWidget,
  ReservationRulesetVariableBuffer,
} from './Components';
import { reservationRulesetsActions } from 'state/modules/reservationRulesets';
import { UserContext } from 'context';
import { initialValues, formSchema } from './utils';
import { uiActions } from 'state/modules/UI';
import { removeEmpty } from 'utils';
import { accessControl } from '../../components/AccessControl';

const ReservationRulesetCreate = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const formRef = useRef(null);
  const history = useHistory();
  const { id, action } = useParams();
  const [firstLoad, setFirstLoad] = useState(true);
  const { currentRuleset } = useSelector((state) => state.reservationRulesets);
  const { ruleset, isFetching, loading: loadingRRS, errors: asyncErrors } = currentRuleset;
  const [position, setPosition] = useState(0);
  const { orgType } = useContext(UserContext);

  const loading = isFetching || loadingRRS;

  const updateReservationRuleset = accessControl(['privilege.reservation_rules_set.update']);
  const createReservationRuleset = accessControl(['privilege.reservation_rules_set.create']);
  const multipleBufferEnabled = accessControl([
    'configuration.reservation.multiple_buffer.enabled.true',
  ]);

  const paymentsEnabled = useSelector((state) => state.config.settings?.core?.receivable?.payments);

  //TODO: DO this at the router level, no need to load the compoenet/make request if the user does not have access
  const redirectToNotFound = !createReservationRuleset && !multipleBufferEnabled;

  useEffect(() => {
    if (redirectToNotFound) {
      history.push(buildUrl('/404'));
    }
  }, [redirectToNotFound]);

  const allowFullEdit = createReservationRuleset && updateReservationRuleset;
  const initialValue = useMemo(
    () => initialValues({ id, ruleset, orgType, allowFullEdit, paymentsEnabled, multipleBufferEnabled }),
    [id, ruleset, orgType, allowFullEdit, paymentsEnabled, multipleBufferEnabled]
  );

  const stepsList = [
    {
      key: 'details',
      label: i18n.t('common.details'),
    },
    ...(multipleBufferEnabled
      ? [
          {
            position: 1,
            key: 'maintenance-buffer',
            label: i18n.t('reservation_rulesets.maintenance_buffer'),
          },
        ]
      : []),
    //Customer (driver_rules) are show only for services
    ...(orgType === 'msServiceOrg'
      ? [
          {
            key: 'customers',
            label: i18n.t('reservation_rulesets.customers'),
          },
        ]
      : []),
    //Non Customers (offline_rules) are show for service and owner
    {
      key: 'non-customers',
      label: i18n.t('reservation_rulesets.non_customers'),
    },
    ...(orgType === 'msServiceOrg' && paymentsEnabled !== false
      ? [
          {
            key: 'payments',
            label: i18n.t('reservation_rulesets.payments'),
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (!isNaN(id)) {
      dispatch(reservationRulesetsActions.fetchReservationRulesetById(id));
    }
  }, [dispatch, id, action]);

  useEffect(() => {
    if (ruleset?.name && action === 'clone' && firstLoad) {
      dispatch(
        reservationRulesetsActions.fetchUpdateReservationRulesetInfo({
          name: `Copy of ${ruleset?.name}` || '',
        })
      );
      setFirstLoad(false);
    }
  }, [ruleset?.name]); // eslint-disable-line

  const onSubmit = async (params) => {
    let response;
    const payload = {
      ...params,
    };

    const emptyToNull = ['view_order'];

    emptyToNull.forEach((key) => {
      if (payload[key] === '') {
        payload[key] = null;
      }
    });

    let cleanPayload = payload;
    orgType !== 'msServiceOrg' &&
      (cleanPayload = removeEmpty(
        payload,
        [],
        ['driver_rules', 'payment_framework', 'payment_framework_settings']
      ));
    paymentsEnabled === false &&
      (cleanPayload = removeEmpty(
        payload,
        [],
        ['payment_framework', 'payment_framework_settings']
      ));
    if (!isNaN(id) && action !== 'clone') {
      response = await dispatch(
        reservationRulesetsActions.updateReservationRuleset(id, cleanPayload)
      );
    } else {
      response = await dispatch(reservationRulesetsActions.createReservationRuleset(cleanPayload));
    }

    if (response?.type?.endsWith('SUCCESS')) {
      history.push(buildUrl('/reservation-rulesets'));
      dispatch(reservationRulesetsActions.resetCurrentRuleset());
    }
  };

  const handleSave = () => {
    const isDirty = formRef?.current?.dirty;
    if (!isNaN(id) && !isDirty) {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: i18n.t('reservation_rulesets.no_updates'),
        })
      );
    } else {
      formRef.current.handleSubmit();
    }
  };

  const handleCancel = () => {
    history.push(buildUrl('/reservation-rulesets'));
  };

  const getTitle = () => {
    if (action !== 'clone' && !isNaN(id)) {
      return i18n.t('reservation_rulesets.edit');
    }
    if (action === 'clone') {
      return i18n.t('reservation_rulesets.clone');
    }
    return i18n.t('reservation_rulesets.create');
  };

  const handleOnNext = () => {
    setPosition((currPosition) => {
      if (currPosition + 1 < stepsList.length) {
        window.scrollTo(0, 0);
        return currPosition + 1;
      } else {
        return currPosition;
      }
    });
  };
  const handleOnPrevious = () => {
    window.scrollTo(0, 0);
    setPosition((currPosition) => currPosition - 1);
  };

  return (
    <Layout title={i18n.t('reservation_rulesets.create')}>
      <Formik
        initialValues={initialValue}
        validationSchema={formSchema(orgType, paymentsEnabled)}
        enableReinitialize={true}
        innerRef={formRef}
        onSubmit={onSubmit}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ handleSubmit, setFieldValue, errors, values, dirty }) => {
          return (
            <Container className={classes.container}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Grid
                    container
                    spacing={2}
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Grid item>
                      <Title title={getTitle()} />
                    </Grid>
                    <Grid item>
                      <Button
                        variant="outlined"
                        color="primary"
                        style={{ textTransform: 'capitalize' }}
                        className={classes.cancelButton}
                        onClick={handleCancel}
                      >
                        {i18n.t('common.cancel')}
                      </Button>
                      <Button
                        style={{ textTransform: 'capitalize' }}
                        variant="contained"
                        color="primary"
                        onClick={handleSave}
                        disabled={loading}
                      >
                        {i18n.t('common.save')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12} container>
                  {dirty ? (
                    <span className={classes.warning}>
                      {i18n.t('reservation_rulesets.unsaved_changes')}
                    </span>
                  ) : (
                    <span style={{ minWidth: 1, minHeight: 24 }} />
                  )}
                </Grid>
                <Grid item xs={12}>
                  <Form noValidate onSubmit={handleSubmit}>
                    {allowFullEdit ? (
                      <Paper elevation={2}>
                        <Wizard
                          exit={stepsList.length - 1 === position}
                          steps={stepsList}
                          loading={loading}
                          title={i18n.t('reservation_rulesets.rules')}
                          position={position}
                          handleExit={handleSave}
                          onNext={handleOnNext}
                          onPrevious={handleOnPrevious}
                          handleEndOnboarding={handleCancel}
                          customStepperClass={classes.stepperClass}
                        >
                          {stepsList[position].key === 'details' && <DetailsInputs />}
                          {stepsList[position].key === 'maintenance-buffer' && (
                            <ReservationRulesetVariableBuffer
                              ruleset={values}
                              loading={loading}
                              editable={true}
                              setFieldValue={setFieldValue}
                            />
                          )}
                          {stepsList[position].key === 'customers' && (
                            <RulesetInputs rule="driver_rules" errors={asyncErrors} />
                          )}
                          {stepsList[position].key === 'non-customers' && (
                            <RulesetInputs rule="offline_rules" errors={asyncErrors} />
                          )}
                          {stepsList[position].key === 'payments' && <PaymentInputs />}
                        </Wizard>
                      </Paper>
                    ) : (
                      <ReservationRulesetVariableBufferWidget
                        ruleset={values}
                        loading={loading}
                        editable={true}
                        setFieldValue={setFieldValue}
                      />
                    )}
                  </Form>
                </Grid>
              </Grid>
            </Container>
          );
        }}
      </Formik>
    </Layout>
  );
};

export { ReservationRulesetCreate };
